import dayjs from "dayjs";
import React from "react";

import {
  ChildMixUpdate,
  DeploymentType,
  Latest,
  MaterialRead,
  NameBasedPeriodMaterialSummary,
  Next,
  ProductionMixResultRead,
  SteelGrade,
  useGetDeployedPlanResultsQuery,
  useGetMaterialMetadataSetQuery,
} from "src/store/api/generatedApi";

import { useTenant } from "hooks/settings";

import { selectSearchedSteelGradeIds } from "src/store/slices/steelGradeSearchSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";

import { EditableTableActions } from "components/common/plan/EditableTableActions";
import { EditableTableSkeleton } from "components/common/plan/EditableTableSkeleton";
import { SupervisorTable } from "components/common/plan/SupervisorTable";

import { useTenantData } from "contexts/tenant";
import {
  planMixTableEditsSlice,
  selectMixIdsWithLayerEdits,
  selectPlanMixTableEditEditedRows,
} from "src/store/slices/planMixesTableEditSlice";
import { mergeRowsAndMix } from "components/common/plan/utils";
import { Row } from "components/common/plan/types";
import { MixLayeringDialog } from "components/common/plan/layerEditor/MixLayeringDialog";

import { typeSafeObjectFromEntries } from "src/utils/typeSafeObjectFromEntries";

import { Stack } from "@mui/material";
import { PlanId } from "components/common/boundary/PlanId";
import { minimumPeriod, Period } from "hooks/periodIndex";
import { PlanNotFound } from "./PlanNotFound";
import { RefreshPlanSnackbar } from "./RefreshPlanSnackbar";
import { useHandleOnSubmit } from "./useHandleOnSubmit";
import { initialDeploymentTypeToDeploymentTypes } from "./utils";
import { CoreDeploymentType } from "components/common/plan/types";

interface ActiveSupervisorPlanProps {
  planTimeframe: Latest | Next;
}

interface PlanProps {
  deploymentType: DeploymentType;
  planTimeframe: Latest | Next;
  id: PlanId;
  period: Period;
  results: ProductionMixResultRead[];
  originalDeploymentMixIds: Record<number, number>;
  startAt: Date;
  endAt: Date;
  periodMaterialSummaries: NameBasedPeriodMaterialSummary[];
  materials: MaterialRead[];
  steelGrades: SteelGrade[];
}

export const ActiveSupervisorPlan = ({
  planTimeframe,
}: ActiveSupervisorPlanProps) => {
  const tenantName = useTenant();
  const period = minimumPeriod;

  const { data: planResults, error } = useGetDeployedPlanResultsQuery({
    tenantName,
    planId: planTimeframe,
    period: period,
    deploymentTypes: initialDeploymentTypeToDeploymentTypes.standard,
  });
  // Shadow query for migration diff coverage (Meticulous). Result unused.
  useGetDeployedPlanResultsQuery({
    tenantName,
    planId: planTimeframe,
    period: period,
    deploymentTypes: initialDeploymentTypeToDeploymentTypes.standard,
  });

  const { data: material_set } = useGetMaterialMetadataSetQuery({
    tenantName,
    materialMetadataSetId: "default",
  });

  if (error && "status" in error && error.status === 404) {
    return (
      <PlanNotFound
        planTimeframe={planTimeframe}
        deploymentGroup="standard"
        deploymentTypes={initialDeploymentTypeToDeploymentTypes.standard}
      />
    );
  }

  if (planResults && material_set) {
    const {
      plan_id,
      periods,
      start_at,
      end_at,
      deployment_type,
      originally_deployed_mix_ids,
      steel_grade_set,
      summary,
    } = planResults;
    const periodResults = periods[period.toString()]?.mixes ?? [];
    return (
      <Plan
        deploymentType={deployment_type}
        key={deployment_type}
        id={plan_id as PlanId}
        period={period}
        results={periodResults}
        originalDeploymentMixIds={originally_deployed_mix_ids}
        startAt={new Date(start_at)}
        endAt={new Date(end_at)}
        planTimeframe={planTimeframe}
        steelGrades={steel_grade_set.steel_grades}
        materials={material_set.materials}
        periodMaterialSummaries={summary.period_material_summary}
      />
    );
  }
  return <EditableTableSkeleton />;
};

export const BackupSupervisorPlan = () => {
  const tenantName = useTenant();
  const period = minimumPeriod;

  const { data: planResults, error } = useGetDeployedPlanResultsQuery({
    tenantName,
    planId: "latest",
    period: period,
    deploymentTypes: initialDeploymentTypeToDeploymentTypes.backup,
  });
  // Shadow query for migration diff coverage (Meticulous). Result unused.
  useGetDeployedPlanResultsQuery({
    tenantName,
    planId: "latest",
    period: period,
    deploymentTypes: initialDeploymentTypeToDeploymentTypes.backup,
  });

  const { data: material_set } = useGetMaterialMetadataSetQuery({
    tenantName,
    materialMetadataSetId: "default",
  });

  if (error && "status" in error && error.status === 404) {
    return (
      <PlanNotFound
        planTimeframe="latest"
        deploymentGroup="backup"
        deploymentTypes={initialDeploymentTypeToDeploymentTypes.backup}
      />
    );
  }

  if (planResults && material_set) {
    const {
      plan_id,
      periods,
      start_at,
      end_at,
      deployment_type,
      originally_deployed_mix_ids,
      steel_grade_set,
      summary,
    } = planResults;
    const periodResults = periods[period.toString()]?.mixes ?? [];
    return (
      <Plan
        planTimeframe="latest"
        deploymentType={deployment_type}
        key={deployment_type}
        id={plan_id as PlanId}
        period={period}
        results={periodResults}
        originalDeploymentMixIds={originally_deployed_mix_ids}
        startAt={new Date(start_at)}
        endAt={new Date(end_at)}
        steelGrades={steel_grade_set.steel_grades}
        materials={material_set.materials}
        periodMaterialSummaries={summary.period_material_summary}
      />
    );
  }
  return <EditableTableSkeleton />;
};

const Plan = ({
  id,
  period,
  results,
  originalDeploymentMixIds,
  startAt,
  endAt,
  deploymentType,
  planTimeframe,
  steelGrades,
  materials,
  periodMaterialSummaries,
}: PlanProps) => {
  if (deploymentType === "purchasing_plan") {
    return null;
  }
  return (
    <PlanContent
      id={id}
      period={period}
      results={results}
      originalDeploymentMixIds={originalDeploymentMixIds}
      startAt={startAt}
      endAt={endAt}
      deploymentType={deploymentType}
      planTimeframe={planTimeframe}
      steelGrades={steelGrades}
      materials={materials}
      periodMaterialSummaries={periodMaterialSummaries}
    />
  );
};

const PlanContent = ({
  id,
  period,
  results,
  originalDeploymentMixIds,
  startAt,
  endAt,
  deploymentType,
  planTimeframe,
  steelGrades,
  materials,
  periodMaterialSummaries,
}: Omit<PlanProps, "deploymentType"> & {
  deploymentType: CoreDeploymentType;
}) => {
  const inventoryByMaterialName = React.useMemo(
    () =>
      typeSafeObjectFromEntries(
        periodMaterialSummaries
          .filter((summary) => summary.period === period)
          .map((summary) => [
            summary.material,
            summary.initial_inventory + summary.obtainable_obtained,
          ])
      ),
    [periodMaterialSummaries, period]
  );

  const filteredSteelGrades = React.useMemo(() => {
    const steelGradeNames = new Set(
      results.flatMap((result) =>
        result.production.map((item) => item.steel_grade_name)
      )
    );
    return steelGrades.filter((steelGrade) =>
      steelGradeNames.has(steelGrade.name)
    );
  }, [steelGrades, results]);

  const deploymentDetails = React.useMemo(():
    | ["backup" | "edited_backup", "latest"]
    | ["standard" | "edited_standard", "latest" | "next"] => {
    switch (deploymentType) {
      case "backup":
      case "edited_backup":
        return [deploymentType, "latest"];
      case "edited_standard":
      case "standard":
        return [deploymentType, planTimeframe];
    }
  }, [deploymentType, planTimeframe]);

  const deploymentGroup = React.useMemo((): "backup" | "standard" => {
    switch (deploymentType) {
      case "backup":
      case "edited_backup":
        return "backup";
      case "standard":
      case "edited_standard":
        return "standard";
    }
  }, [deploymentType]);

  const deploymentTypes: DeploymentType[] = React.useMemo(() => {
    switch (deploymentType) {
      case "standard":
      case "edited_standard":
        return ["standard", "edited_standard"];
      case "backup":
      case "edited_backup":
        return ["backup", "edited_backup"];
    }
  }, [deploymentType]);

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const doSubmit = useHandleOnSubmit(startAt, endAt, id, deploymentType);

  const hasDri = React.useMemo(() => {
    return materials.some(
      (material) => material.addition_location === "hopper"
    );
  }, [materials]);
  const basketMaterials = React.useMemo(() => {
    return materials
      .filter((material) => material.addition_location === "basket")
      .filter((material) => material.visibility !== "hidden")
      .sort(
        (materialA, materialB) =>
          materialA.deployment_ordering - materialB.deployment_ordering
      )
      .map((material) => ({
        id: material.id,
        name: material.name,
        inventory: inventoryByMaterialName[material.name] ?? null,
      }));
  }, [materials, inventoryByMaterialName]);

  const selectedSteelGradeIds = useAppSelector((state) =>
    selectSearchedSteelGradeIds(state, id, period, "supervisor")
  );

  const dispatch = useAppDispatch();
  const { layer_edit_mode } = useTenantData();
  const editedRows = useAppSelector((state) =>
    selectPlanMixTableEditEditedRows(state, id, period)
  );
  const mixIdsWithLayerEdits = useAppSelector((state) =>
    selectMixIdsWithLayerEdits(state, id, period)
  );
  const [layerDialogMixId, setLayerDialogMixId] = React.useState<number | null>(
    null
  );
  const layerEditing = React.useMemo(
    () =>
      layer_edit_mode === "none"
        ? undefined
        : {
            onEditLayers: (mixId: number) => {
              setLayerDialogMixId(mixId);
            },
            hasLayerEdits: (mixId: number) =>
              mixIdsWithLayerEdits.includes(mixId),
          },
    [layer_edit_mode, mixIdsWithLayerEdits]
  );
  const dialogMixResult = React.useMemo(() => {
    if (layerDialogMixId === null) return null;
    const original = results.find(
      (result) => result.mix.id === layerDialogMixId
    );
    return original ? mergeRowsAndMix(editedRows, original) : null;
  }, [layerDialogMixId, results, editedRows]);

  const handleLayerApply = (mixId: number, update: ChildMixUpdate) => {
    const inlineMix = update.new_mix_production.mix;
    if (!inlineMix) return;
    const layersByBasketNumber = new Map(
      inlineMix.baskets.map(
        (basket) => [basket.basket_number, basket.layers] as const
      )
    );
    const newRows: Row[] = editedRows.map((row) => {
      if (row.mixId !== mixId) return row;
      if (row.type === "profile") {
        const newLayers = layersByBasketNumber.get(1);
        return newLayers !== undefined ? { ...row, layers: newLayers } : row;
      }
      if (row.type === "basket") {
        const newLayers = layersByBasketNumber.get(row.basketNumber);
        return newLayers !== undefined ? { ...row, layers: newLayers } : row;
      }
      return row;
    });
    dispatch(planMixTableEditsSlice.actions.update([id, period, newRows]));
    dispatch(
      planMixTableEditsSlice.actions.markMixAsLayerEdited([id, period, mixId])
    );
  };

  const handleOnSubmit = async (mixes: ChildMixUpdate[], comment: string) => {
    const layerSource =
      mixIdsWithLayerEdits.length > 0 ? "manual" : "per_basket_totals";
    setIsSubmitting(true);
    try {
      await doSubmit(mixes, comment, layerSource);
      if (layerSource === "manual") {
        dispatch(
          planMixTableEditsSlice.actions.clearAllLayerEditMarks([id, period])
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const timeString = `${dayjs(startAt).format("MMM D YYYY HH:mm")} - ${dayjs(
    endAt
  ).format("MMM D YYYY HH:mm")}`;

  return (
    <Stack sx={{ minHeight: "100%" }}>
      <EditableTableActions
        timeString={timeString}
        view="supervisor"
        materials={materials}
        showDriConsumption={false}
        planId={id}
        period={period}
        steelGrades={filteredSteelGrades}
        onSubmit={handleOnSubmit}
        results={results}
      >
        {(onChange, _, isSubmitting, __, rows) => {
          const [deploymentType, planTimeframe] = deploymentDetails;
          return (
            <SupervisorTable
              deploymentType={deploymentType}
              planId={id}
              planTimeframe={planTimeframe}
              onChange={(rows) => {
                onChange(id, period, rows);
              }}
              disableEdit={isSubmitting}
              rows={rows}
              originalDeploymentMixIds={originalDeploymentMixIds}
              materials={basketMaterials}
              selectedSteelGradeIds={selectedSteelGradeIds}
              startAt={startAt}
              endAt={endAt}
              hasDri={hasDri}
              layerEditing={layerEditing}
            />
          );
        }}
      </EditableTableActions>
      <MixLayeringDialog
        open={layerDialogMixId !== null}
        mixResult={dialogMixResult}
        materials={materials}
        onApply={(update) => {
          if (layerDialogMixId !== null) {
            handleLayerApply(layerDialogMixId, update);
          }
        }}
        onClose={() => {
          setLayerDialogMixId(null);
        }}
      />
      {!isSubmitting ? (
        <RefreshPlanSnackbar
          planTimeframe={planTimeframe}
          deploymentTypes={deploymentTypes}
          deploymentGroup={deploymentGroup}
          currentPlanId={id}
        />
      ) : null}
    </Stack>
  );
};
